import { useGetFamilyPriceMatrixApi } from 'apis/supplier-prices';
import { Box, Flex, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from 'design-system';
import { AddSupplierControl } from 'pages/prices/components/add-supplier-control';
import { PriceCell } from 'pages/prices/components/price-cell';
import type { MatrixSupplier } from 'pages/prices/utils/build-price-matrix';
import { buildPriceMatrix } from 'pages/prices/utils/build-price-matrix';
import { useEffect, useMemo, useState } from 'react';

export interface VariantPriceEditorProps {
  masterProductId: number;
  familyId: number;
}

// Variant-scoped price editor for the Daily Update List expansion: one row
// per supplier quoting THIS variant, the price being the matrix's own
// editable PriceCell (commit updates the deal; an empty row's first price
// creates it). Reuses the family-matrix read model filtered to the variant —
// PriceCell's commit invalidates that query, so the editor refreshes itself.
export const VariantPriceEditor = ({ masterProductId, familyId }: VariantPriceEditorProps) => {
  const { data, isLoading } = useGetFamilyPriceMatrixApi(familyId);

  const { suppliers, cells, variant } = useMemo(() => {
    const variants = (data?.data?.variants ?? []).filter((v) => v.id === masterProductId);
    const deals = (data?.data?.deals ?? []).filter((deal) => deal.masterProductId === masterProductId);
    const matrix = buildPriceMatrix(variants, deals);
    return { suppliers: matrix.suppliers, cells: matrix.rows[0]?.cells ?? {}, variant: variants[0] };
  }, [data, masterProductId]);

  // Suppliers added locally that have no deal yet — they materialize in
  // `suppliers` once their first price lands, so drop those to avoid doubles
  // (same pattern as the matrix).
  const [addedSuppliers, setAddedSuppliers] = useState<MatrixSupplier[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  useEffect(() => {
    setAddedSuppliers([]);
    setIsAddOpen(false);
  }, [masterProductId]);

  const rows = useMemo(() => {
    const dealSupplierIds = new Set(suppliers.map((supplier) => supplier.id));
    return [...suppliers, ...addedSuppliers.filter((supplier) => !dealSupplierIds.has(supplier.id))];
  }, [suppliers, addedSuppliers]);

  if (isLoading) {
    return (
      <Flex justify='center' align='center' py='24px'>
        <Spinner size='sm' color='red.500' />
      </Flex>
    );
  }

  return (
    <Box p='12px 16px 16px'>
      {rows.length ? (
        <Table size='sm' maxW='520px'>
          <Thead>
            <Tr>
              <Th>Supplier</Th>
              <Th isNumeric w='170px'>
                Price
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((supplier) => (
              <Tr key={supplier.id}>
                <Td fontSize='13px' color='gray.800'>
                  {supplier.name || `#${supplier.id}`}
                </Td>
                <Td p={0}>
                  <PriceCell
                    cell={cells[supplier.id]}
                    supplierId={supplier.id}
                    masterProductId={masterProductId}
                    variant={variant}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text fontSize='13px' color='gray.500' mb='10px'>
          No supplier prices for this product yet — add the first one below.
        </Text>
      )}

      <Box mt='12px'>
        <AddSupplierControl
          excludeIds={rows.map((supplier) => supplier.id)}
          isOpen={isAddOpen}
          onOpen={() => setIsAddOpen(true)}
          onCancel={() => setIsAddOpen(false)}
          onAdd={(supplier) => {
            setAddedSuppliers((prev) => [...prev, supplier]);
            setIsAddOpen(false);
          }}
        />
      </Box>
    </Box>
  );
};
